import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BedDouble, ArrowUpRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import SectionHeading from "./SectionHeading";

const ease = [0.16, 1, 0.3, 1];
const externalUrl = (u) => (u && !/^https?:\/\//i.test(u) ? `https://${u}` : u);
const openExternal = (e, u) => { e.preventDefault(); const url = externalUrl(u); if (url) window.open(url, "_blank", "noopener,noreferrer"); };

export default function Hotel() {
  const wedding = useContent();
  const hotel = wedding.hotel || {};
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="hospedaje" ref={ref} className="bg-ink text-white py-20 sm:py-28 grain relative">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <SectionHeading number="04" kicker="Dónde quedarse" title="Hospedaje" dark />

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          {/* Image */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="relative overflow-hidden aspect-[4/5] sm:aspect-[16/10] bg-white/5">
              <motion.img
                src={hotel.image}
                alt={hotel.name}
                style={{ y: imgY }}
                className="absolute inset-0 w-full h-[116%] object-cover"
                data-testid="hotel-image"
              />
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-5 order-1 lg:order-2 lg:pb-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease }}
            >
              <p className="overline text-white/50">Recomendación</p>
              <h3 className="font-display text-3xl sm:text-4xl mt-4 tracking-tight">
                {hotel.name}
              </h3>
              <p className="mt-6 font-light text-white/70 max-w-sm leading-relaxed">
                {hotel.description}
              </p>
              {hotel.address && (
                <p className="mt-6 font-light text-white/70 max-w-sm leading-relaxed flex gap-3">
                  <BedDouble size={18} strokeWidth={1.4} className="mt-1 shrink-0" />
                  <span>{hotel.address}</span>
                </p>
              )}
              {hotel.note && (
                <p className="mt-4 text-sm font-light text-white/50 max-w-sm">
                  {hotel.note}
                </p>
              )}

              {hotel.bookingUrl && (
                <a
                  href={externalUrl(hotel.bookingUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => openExternal(e, hotel.bookingUrl)}
                  className="group mt-10 inline-flex items-center gap-3 bg-white text-ink px-8 py-4 hover:bg-transparent hover:text-white border border-white transition-colors duration-300"
                  data-testid="hotel-booking-link"
                >
                  <span className="overline">Reservar</span>
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
